'use client'

/**
 * Nova AI Chat Component
 * 
 * A chat interface for interacting with Nova AI for data analysis and query building.
 */

import * as React from 'react';
import { 
  Input,
  ScrollArea,
  ScrollBar,
  cn,
} from '../../../components/ui';
import { toast } from '../../../components/ui/toast';
import { Send, Sparkles } from 'lucide-react';
import { useChat, type Message } from '@ai-sdk/react';
import { novaAiTools } from '../../../lib/ai/ai-tools';
import { NovaAIChatProps, ToolCall, SQLQueryResponse, SchemaResponse, supernovaColor, SubscriptionInfo } from './types';
import { executeSqlQuery, getSchema } from './utils';
import { MessageContent } from './components/message-content';
import { PanelWidthObserver } from '../nova/panel-width-observer';
import { ChatTypingIndicator } from './components/chat-typing-indicator';
import { ChatMessage } from './components/chat-message';
import { ChatInput } from './components/chat-input';
import { ChatSuggestions } from './components/chat-suggestions';
import { Engine } from '../../../lib/engine';
import { Button } from '../../../components/ui/button';

/**
 * Nova AI Chat Inner Component
 */
export function NovaAIChatInner({
  className,
  chatEndpoint = '/api/chat',
  engine,
  onRunSqlQuery,
  initialSchema,
  subscriptionInfo,
  onUpgradeClick,
  initialPrompt
}: NovaAIChatProps) {
  // State for schema initialization
  const [isInitializingSchema, setIsInitializingSchema] = React.useState<boolean>(!initialSchema);
  const [schemaError, setSchemaError] = React.useState<string | null>(null);
  const [schema, setSchema] = React.useState<SchemaResponse | null>(initialSchema || null);
  
  // Local input state to bypass useChat's handleInputChange issues
  const [localInput, setLocalInput] = React.useState<string>('');
  
  // Ref for scrolling to bottom of chat
  const messagesEndRef = React.useRef<HTMLDivElement>(null);
  
  // Ref for the input element to maintain focus
  const inputRef = React.useRef<HTMLInputElement>(null);
  
  // Ref for the form element (needed for compatibility with ChatInput)
  const formRef = React.useRef<HTMLFormElement>(null);
  
  // Helper function to check if an interactive element has focus
  const isInteractiveElementFocused = (): boolean => {
    const activeElement = document.activeElement;
    return Boolean(
      activeElement && (
        activeElement.tagName === 'INPUT' ||
        activeElement.tagName === 'TEXTAREA' ||
        activeElement.tagName === 'SELECT' ||
        activeElement.tagName === 'BUTTON' ||
        activeElement.hasAttribute('contenteditable') ||
        activeElement.getAttribute('role') === 'button'
      )
    );
  };
  
  // Create initial messages with system instructions and a welcome message
  const initialMessages = React.useMemo(() => {
    const messages: Message[] = [
      // System instructions
      {
        id: 'system-instructions',
        role: 'system',
        content: `You are Nova AI, a helpful data analysis assistant.

IMPORTANT: You MUST ALWAYS get the schema of the database FIRST before answering ANY questions. This is your TOP PRIORITY.
Your very first action in any conversation MUST be to call the getSchema tool.
DO NOT wait for the user to tell you to do so.
DO NOT respond to user queries until you have the schema.

If you don't have a required table, then please call getSchema again to see if it was added.

Always explain your thought process and the insights from the data in a clear, concise manner.
When showing SQL queries to the user, format them properly for readability.`
      }
    ];
    
    // If we have schema data, add it to the system instructions
    if (initialSchema && initialSchema.success && initialSchema.tables) {
      // Add schema information to system instructions
      messages.push({
        id: 'system-schema-info',
        role: 'system',
        content: `Here is the database schema information:
${JSON.stringify(initialSchema, null, 2)}`
      });
      
      // Add a welcome message with schema information
      const tableCount = initialSchema.tables.length;
      const tableNames = initialSchema.tables.map(table => table.name).join(', ');
      
      // Create a welcome message with schema information
      // Use 'any' type to add custom properties for the UI
      const welcomeMessage: any = {
        id: 'welcome',
        role: 'assistant',
        content: `Hello! I'm Nova AI. I've loaded the schema information. I can see ${tableCount} table${tableCount !== 1 ? 's' : ''} in the database: ${tableNames}. How can I help you with your data analysis today?`,
        // Add tool call parts to render the schema in the UI
        parts: [
          {
            type: 'text',
            text: `Hello! I'm Nova AI. I've loaded the schema information. I can see ${tableCount} table${tableCount !== 1 ? 's' : ''} in the database: ${tableNames}. How can I help you with your data analysis today?`
          },
          {
            type: 'tool-invocation',
            toolInvocation: {
              toolName: 'getSchema',
              toolCallId: 'initial-schema-display',
              args: {},
              state: 'result',
              result: initialSchema
            }
          }
        ]
      };
      
      messages.push(welcomeMessage);
    } else {
      // Add a simple welcome message if no schema is provided
      messages.push({
        id: 'welcome',
        role: 'assistant',
        content: 'Hello! I\'m Nova AI. I\'ll help you analyze your data. Let me fetch the database schema first to see what data we have available.'
      });
    }
    
    return messages;
  }, [initialSchema]);
  
  // Handle tool calls
  const handleToolCall = async (params: any): Promise<SQLQueryResponse | SchemaResponse> => {
    console.log('Tool call received:', params);
    
    try {
      // Extract the toolCall object
      const toolCall = params.toolCall;
      
      if (!toolCall) {
        throw new Error('No tool call provided');
      }
      
      // Get the tool name and parameters from the nested toolCall object
      const toolName = toolCall.toolName;
      const args = toolCall.args || {};
      
      if (!toolName) {
        console.warn('Tool call missing name:', toolCall);
        throw new Error('Tool call missing name');
      }
      
      console.log(`Executing tool: ${toolName}`, args);
      
      // Handle SQL query tool
      if (toolName === 'sqlQuery') {
        if (!('query' in args)) {
          return {
            success: false,
            error: 'Please provide a valid SQL query in the "query" parameter.'
          };
        }
        
        const queryString = String(args.query).trim();
        
        if (!queryString) {
          return {
            success: false,
            error: 'Please provide a non-empty SQL query.'
          };
        }
        
        try {
          const result = await executeSqlQuery(queryString, engine);
          
          // Add debug information about the tool call structure
          console.log('Tool call structure for SQL query:', toolCall);
          console.log('SQL query result with query:', result);
          
          return result;
        } catch (sqlError) {
          console.error('SQL execution error:', sqlError);
          console.error('SQL QUERY THAT FAILED:', queryString);
          
          // Return error message that asks the LLM to try again with better arguments
          const errorMessage = sqlError instanceof Error ? sqlError.message : 'Failed to execute SQL query';
          console.error('RETURNING ERROR TO LLM:', errorMessage);
          
          return {
            success: false,
            error: `I couldn't execute this SQL query due to an error: ${errorMessage}. Please try again with a corrected query.`,
            query: queryString
          };
        }
      }
      
      // Handle get schema tool
      if (toolName === 'getSchema') {
        try {
          
          const schemaResult = await getSchema(engine);
          
          // Update the schema state so it's available for future reference
          setSchema(schemaResult);
          setIsInitializingSchema(false);
          
          
          return schemaResult;
        } catch (schemaError) {
          console.error('Schema retrieval error:', schemaError);
          return {
            success: false,
            error: 'I couldn\'t retrieve the schema information. Please try again.'
          };
        }
      }
      
      return {
        success: false,
        error: `I don't know how to use the "${toolName}" tool. Please try using either "sqlQuery" or "getSchema" instead.`
      };
    } catch (error) {
      console.error('Error in handleToolCall:', error);
      return {
        success: false,
        error: 'Error processing tool call'
      };
    }
  };
  
  // Use the Vercel AI SDK useChat hook
  const { 
    messages, 
    input, 
    handleInputChange, 
    handleSubmit: originalHandleSubmit, 
    isLoading,
    error: chatApiError,
    append,
    stop
  } = useChat({
    api: chatEndpoint,
    initialMessages,
    // Enable multi-step tool calls
    maxSteps: 10,
    // Handle tool calls
    onToolCall: handleToolCall,
    // Handle errors with special care for auth issues vs tool call failures
    onError: (error) => {
      console.error('Chat error:', error);
      
      // Check if this is an authentication or subscription error
      const errorWithResponse = error as any;
      if (errorWithResponse.response) {
        const status = errorWithResponse.response.status;
        console.log(`Error status: ${status}`);
        
        // For auth/subscription errors (401/403), NEVER retry
        if (status === 401 || status === 403) {
          console.log('Auth error - stopping all requests and NOT retrying');
          stop(); // Stop any ongoing requests
          
          // Show toast notification for auth/subscription errors with close button
          if (status === 401) {
            toast("Authentication required. Please log in to use Nova AI.", {
              closeButton: true,
              duration: 5000
            });
          } else if (status === 403) {
            toast("Subscription required. Please upgrade your account to use Nova AI.", {
              closeButton: true,
              duration: 5000
            });
          }
          
          return; // Exit without appending any messages
        }
      }
      
      // For tool call errors (like SQL failures), let the LLM fix them
      // Check if the error is related to a tool call
      const isToolCallError = error.message && (
        error.message.includes('tool') || 
        error.message.includes('sql') || 
        error.message.includes('query') ||
        error.message.includes('table') ||
        error.message.includes('catalog')
      );
      
      console.error('ERROR MESSAGE:', error.message);
      console.error('IS TOOL CALL ERROR?', isToolCallError);
      
      if (isToolCallError) {
        console.error('🔄 TOOL CALL ERROR DETECTED - PROMPTING LLM TO RETRY 🔄');
        console.error('Error message:', error.message);
        console.error('Appending system message to prompt LLM to fix and retry');
        
        // Add a system message to prompt the LLM to fix the tool call
        append({
          role: 'system',
          content: `[INTERNAL] The tool call failed with error: ${error.message}. Please fix the parameters and try again.`
        });
        
        // Log the messages array to see if the system message was added
        console.error('MESSAGES AFTER APPEND:', messages);
      } else {
        // For other errors, stop and don't retry
        console.log('Other error - stopping requests');
        stop();
        
        // Show toast notification for other errors with close button
        // Use getErrorMessage to get a user-friendly error message
        const errorMessage = error.message || "An error occurred while connecting to Nova AI. Please try again.";
        const status = errorWithResponse?.response?.status;
        
        // Only show toast for non-tool call errors
        if (status === 429) {
          toast("Too many requests. Please wait a moment and try again.", {
            closeButton: true,
            duration: 5000
          });
        } else if (status >= 500) {
          toast("Server error. Our team has been notified and is working on it.", {
            closeButton: true,
            duration: 5000
          });
        } else if (!isToolCallError) {
          // For any other non-tool call errors
          toast(errorMessage, {
            closeButton: true,
            duration: 5000
          });
        }
      }
    },
    // Add response handler to check for auth errors
    onResponse: (response) => {
      if (response.status === 401) {
        console.log('Authentication required - user not logged in');
        stop(); // Ensure we stop on auth errors
        
        // Show toast notification for auth error with close button
        toast("Authentication required. Please log in to use Nova AI.", {
          closeButton: true,
          duration: 5000
        });
      } else if (response.status === 403) {
        console.log('Subscription required - user does not have an active subscription');
        stop(); // Ensure we stop on subscription errors
        
        // Show toast notification for subscription error with close button
        toast("Subscription required. Please upgrade your account to use Nova AI.", {
          closeButton: true,
          duration: 5000
        });
      } else if (response.status === 429) {
        console.log('Rate limit exceeded');
        stop(); // Stop on rate limit errors
        
        // Show toast notification for rate limit error
        toast("Too many requests. Please wait a moment and try again.", {
          closeButton: true,
          duration: 5000
        });
      } else if (response.status >= 500) {
        console.log('Server error');
        stop(); // Stop on server errors
        
        // Show toast notification for server error
        toast("Server error. Our team has been notified and is working on it.", {
          closeButton: true,
          duration: 5000
        });
      }
    }
  });
  
  // Set initial input value from initialPrompt if provided (only once)
  React.useEffect(() => {
    if (initialPrompt) {
      setLocalInput(initialPrompt);
    }
  }, [initialPrompt]);
  

  
  // Get a user-friendly error message based on the error
  const getErrorMessage = () => {
    if (!chatApiError) return null;
    
    const errorWithResponse = chatApiError as any;
    if (errorWithResponse.response) {
      const status = errorWithResponse.response.status;
      
      if (status === 401) {
        return "Authentication required. Please log in to use Nova AI.";
      } else if (status === 403) {
        return "Subscription required. Please upgrade your account to use Nova AI.";
      } else if (status === 429) {
        return "Too many requests. Please wait a moment and try again.";
      } else if (status >= 500) {
        return "Server error. Our team has been notified and is working on it.";
      }
    }
    
    // Generic error message as fallback
    return "An error occurred while connecting to Nova AI. Please try again.";
  };
  
  // Only show error UI for auth/subscription errors
  const shouldShowErrorUI = () => {
    if (!chatApiError) return false;
    
    const errorWithResponse = chatApiError as any;
    if (errorWithResponse.response) {
      const status = errorWithResponse.response.status;
      // Only show error UI for auth/subscription errors
      return status === 401 || status === 403;
    }
    
    return false;
  };
  
  // Custom submit handler to maintain focus on input and handle errors
  const handleSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    
    // If there's an error, clear it first before submitting again
    if (chatApiError) {
      console.log('Clearing previous error before submitting');
      // We can't directly clear the error, but we can stop any ongoing requests
      stop();
      
      // Wait a moment before allowing a new submission
      setTimeout(() => {
        // Only submit if there's actual input and we're not already loading
        if (localInput.trim() && !isLoading) {
          // Set the useChat input and submit
          handleInputChange({ target: { value: localInput } } as React.ChangeEvent<HTMLInputElement>);
          // Call the original submit handler
          originalHandleSubmit(e);
          setLocalInput(''); // Clear local input after submission
        }
      }, 500); // Wait 500ms before allowing a new submission
      
      return;
    }
    
    // Only submit if there's actual input and we're not already loading
    if (localInput.trim() && !isLoading) {
      // Set the useChat input and submit
      handleInputChange({ target: { value: localInput } } as React.ChangeEvent<HTMLInputElement>);
      // Call the original submit handler
      originalHandleSubmit(e);
      setLocalInput(''); // Clear local input after submission
    }
  };

  // Scroll to bottom of chat when messages change
  React.useEffect(() => {
    if (messagesEndRef.current) {
      messagesEndRef.current.scrollIntoView({ behavior: 'smooth' });
    }
    
    // Refocus the input field when messages change (after a response is received)
    // but only if no other interactive element has focus
    if (inputRef.current && !isLoading) {
      // Use a slightly longer timeout to ensure the UI has settled
      setTimeout(() => {
        // Only refocus if not interacting with another interactive element
        if (inputRef.current && !isInteractiveElementFocused()) {
          inputRef.current.focus();
        }
      }, 200);
    }
  }, [messages, isLoading]);
  
  // Focus the input field when the component mounts
  // but only if no other element has focus
  React.useEffect(() => {
    if (inputRef.current) {
      // Check if any element already has focus
      const activeElement = document.activeElement;
      const bodyHasFocus = activeElement === document.body;
      
      // Only focus if nothing else has focus (body has focus)
      if (bodyHasFocus) {
        inputRef.current.focus();
      }
    }
  }, []);
  
  // Specifically handle refocusing when loading state changes from true to false
  React.useEffect(() => {
    // Only run this effect when isLoading changes from true to false
    if (!isLoading && inputRef.current) {
      // Use a timeout to ensure the UI has fully updated
      const timeoutId = setTimeout(() => {
        // Only refocus if not interacting with another interactive element
        if (inputRef.current && !isInteractiveElementFocused()) {
          inputRef.current.focus();
        }
      }, 300);
      
      return () => clearTimeout(timeoutId);
    }
  }, [isLoading]);
  
  // Handle suggestion click
  const handleSuggestionClick = React.useCallback(({ suggestion, openTrigger }: { suggestion: string; openTrigger: 'chat_suggestion' }) => {
    // Set the local input value first
    setLocalInput(suggestion);
    
    // Then show upgrade modal for free users
    if (!subscriptionInfo?.hasActiveSubscription && onUpgradeClick) {
      onUpgradeClick({ openTrigger });
    }
  }, [subscriptionInfo?.hasActiveSubscription, onUpgradeClick]);
  
  // Render UI based on state
  let content;
  
  // Show error state for schema initialization
  if (schemaError) {
    content = (
      <div className="p-4 text-center text-destructive">
        {schemaError}
        <button 
          className="ml-2 px-2 py-1 rounded bg-primary text-primary-foreground"
          onClick={() => window.location.reload()}
        >
          Retry
        </button>
      </div>
    );
  }
  // Show chat UI
  else {
    content = (
      <div 
        className={cn("flex flex-col h-full overflow-hidden", className)}
      >
        {/* Chat Header with simple styling */}
        <div className="flex items-center gap-2 p-3 border-b bg-card">
          <Sparkles className="h-5 w-5 text-supernova-purple" />
          <h3 className="font-semibold text-supernova-purple">Nova AI</h3>
        </div>
        
        {/* Messages Container */}
        <div className="flex-1 overflow-hidden pt-2 bg-background">
          <ScrollArea className="h-full w-full">
            <div className="space-y-6 px-4 py-5">
              {/* Filter out system messages when rendering */}
              {messages
                .filter(message => message.role !== 'system')
                .map((message) => {
                  // For each message, create a unique key
                  const messageKey = message.id;
                  
                  return (
                    <div key={messageKey} className="w-full">
                      <MessageContent 
                        message={message} 
                        onRunSqlQuery={onRunSqlQuery}
                      />
                    </div>
                  );
                })}
              
              {/* Show error message if there's an API error (only for auth/subscription errors) */}
              {shouldShowErrorUI() && (
                <div className="p-3 text-center text-sm text-destructive bg-destructive/10 rounded border border-destructive/20 my-2">
                  <p className="font-medium">Error</p>
                  <p>{getErrorMessage()}</p>
                </div>
              )}
              
              {/* Loading indicator for assistant response */}
              {isLoading && (
                <ChatTypingIndicator className="ml-0" />
              )}
              
              {/* Invisible div for scrolling to bottom */}
              <div ref={messagesEndRef} />
            </div>
          </ScrollArea>
        </div>
        
        {/* Input Form */}
        <div className="border-t bg-background">
          {/* Show suggestions for free users */}
          {!subscriptionInfo?.hasActiveSubscription && !messages.some(m => m.role === 'user') && (
            <ChatSuggestions 
              onSuggestionClick={handleSuggestionClick}
              className="border-b"
            />
          )}

          {/* Subscription Banner - Only shown for non-subscribers */}
          {!subscriptionInfo?.hasActiveSubscription && (
            <div 
              className="p-2 text-center text-sm bg-muted border-b border-border"
            >
              <span className="text-muted-foreground">
                <button 
                  onClick={() => onUpgradeClick?.({ openTrigger: 'upgrade_button' })}
                  className="font-medium underline text-primary hover:text-primary/80 bg-transparent border-none p-0 cursor-pointer"
                >
                  Upgrade
                </button>
                {" to use Nova AI"}
              </span>
            </div>
          )}
          
          <ChatInput
            className="p-4"
            value={localInput}
            onChange={(value) => {
              setLocalInput(value);
            }}
            onSubmit={() => {
              if (localInput.trim() && !isLoading) {
                // Use append to send the message directly
                append({
                  role: 'user',
                  content: localInput.trim()
                });
                setLocalInput(''); // Clear local input after submission
              }
            }}
            isLoading={isLoading}
            hasActiveSubscription={subscriptionInfo?.hasActiveSubscription}
            placeholder={{
              active: "Ask Nova AI about your data...",
              inactive: "Upgrade to use Nova AI"
            }}
            isInteractiveElementFocused={isInteractiveElementFocused}
          />
          
          {/* Hidden form for compatibility with existing code */}
          <form 
            ref={formRef}
            onSubmit={handleSubmit}
            className="hidden"
          />
        </div>
      </div>
    );
  }
  
  // Wrap the content with the PanelWidthObserver
  return (
    <PanelWidthObserver panel="chat">
      {content}
    </PanelWidthObserver>
  );
}

// Re-export the NovaAIChat from the wrapper
export { NovaAIChat } from './nova-ai-chat-wrapper'; 